import {AxiosError, AxiosInstance, AxiosRequestConfig} from 'axios';
import {NetworkError} from '@/core/Types/error';
import {BaseHeaders, ElementURL, GenericMethod, ResProtocol, UrlParams} from '@/core/Types/IRepository';
import {baseVersion} from '@/constants';
import {severErrors} from '@/core/standard/constants';

export default class BaseRepository {
  [propName: string]: any

  public useAuth: boolean = true;
  public readonly provider: AxiosInstance;
  public collection: string = '';
  public headers: BaseHeaders = {
    'Site': location.origin,
    //'Site': 'https://dev2.foodeodev.com',
    'Client': 'web',
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Accept-language': 'es',
  };
  public version: string = baseVersion;

  constructor(obj: AxiosInstance) {
    this.provider = obj;
  }

  // // Métodos Predefinidos
  public async getAll<T = any>(params: UrlParams<T>) {
    const {url, headers} = this.generateOptionObject(params);
    try {
      const {data: response} = await this.provider.get(url, {params: params.query, headers});
      return response;
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async getBy<T = any>(params: ElementURL<T>) {
    const {url, headers} = this.generateOptionObject(params);
    try {
      const {data: response} = await this.provider.get(url, {params: params.query, headers});
      return response;
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async deleteBy<T = any>(params: ElementURL<T>) {
    const {url, headers} = this.generateOptionObject(params);
    try {
      const {data: response} = await this.provider.delete(url, {params: params.query, headers});
      return response;
    } catch (e) {
      return this.handleError(e);
    }
  }

  // For soft Delete
  public async restoreBy<T = any>(params: ElementURL<T>) {
    const {url, headers} = this.generateOptionObject(params);
    try {
      const {data: response} = await this.provider.delete(url, {params: params.query, headers});
      return response;
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async batchDelete<T = any>(params: UrlParams<T>) {
    const {url, headers} = this.generateOptionObject(params);
    try {
      const {data: response} = await this.provider.delete(url, {params: params.query, headers});
      return response;
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async insert<T = any>(params: UrlParams<T>) {
    const {url, headers} = this.generateOptionObject(params);
    try {
      const {data: response} = await this.provider.post(url, params.query, {headers});
      return response;
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async update<T = any>(params: ElementURL<T>) {
    const {url, headers} = this.generateOptionObject(params);
    try {
      const {data: response} = await this.provider.put(url, params.query, {headers});
      return response;
    } catch (e) {
      return this.handleError(e);
    }
  }

  // For compatibility reasons with older api version
  public async request<T = ResProtocol>(parameters: GenericMethod<T>) {
    const {url, headers, responseType} = this.generateOptionObject(parameters);
    try {
      const config: AxiosRequestConfig = {
        url,
        headers,
        responseType,
        method: parameters.method,
        data: parameters.query,
      };
      // tslint:disable-next-line:max-line-length
      if (parameters.method === 'GET' || parameters.method === 'get' || parameters.method === 'DELETE' || parameters.method === 'delete') {
        config.params = parameters.query;
      }
      const {data: response} = await this.provider.request<T>(config);
      return response;
    } catch (e) {
      return this.handleError(e);
    }
  }

  public generateFinalURL(id: string | number | undefined, url: string | undefined, version: string = this.version, defaulParamsSerializer: boolean = false) {
    let finalURL = version ? `${version}/${this.collection}/` : `${this.collection}/`;
    if (id) {
      finalURL += `${id}/`;
      finalURL += url ? `${url}/` : '';
    } else {
      if (url) {
        finalURL += `${url}/`;
      }
    }
    if(defaulParamsSerializer) {
        this.provider.defaults.paramsSerializer = undefined;
    } else {
        this.provider.defaults.paramsSerializer = this.param2query;
    }
    return finalURL;
  }

  public param2query(params: Record<string, string | number | boolean>): string {

    const base64 = btoa(JSON.stringify(params));
    return `q=${base64}`;
    // return Object.entries(params).map(([key, value]) => `${key}=${value.toString()}`).join('&');
  }

  private handleError(e: AxiosError<ResProtocol>) {
    if (e.response) {
      return new severErrors[e.response.status](e.response.data);
    } else if (e.request) {
      return new NetworkError(e.request);
    } else {
      throw new Error('Something happened while configuring urls please check config status');
    }
  }

  private generateOptionObject({url, version, id, headers, useAuth, responseType, defaulParamsSerializer}: GenericMethod) {
    const token = this.needsAuth(useAuth);
    const finalURL = this.generateFinalURL(id, url, version, defaulParamsSerializer);
    const finalHeaders = this.generateFinalHeaders(headers, token);
    return {
      url: finalURL,
      headers: finalHeaders,
      responseType: responseType || 'json',
    };
  }

  private needsAuth(useAuth: boolean | undefined = this.useAuth) {
    const local = localStorage.getItem('token')?.trim();
    if (local && useAuth) {
      return local;
    }
    return false;
  }

  private generateFinalHeaders(headers: Record<string, string> = {}, auth: string | false) {
    const TPV = localStorage.getItem('tpv');
    const baseHeaders = Object.assign({}, this.headers);
    if (TPV) {
      baseHeaders.TPV = TPV;
    }
    if (!!auth) {
      baseHeaders.Authorization = auth;
    } else {
      if ('Authorization' in baseHeaders) {
        delete baseHeaders.Authorization;
      }
    }
    return Object.assign(baseHeaders, headers);
  }

}
